import { Star } from 'lucide-react'
import { sizedImageUrl } from '../../lib/remoteImage'
import { SOURCE_META, sourceKey } from './reviewSources'
import { countryCode, flagImage } from '../../lib/countryFlag'
import './review-cards.css'

// Review cards shared by the homepage section and /reviews. Rows come from
// GET /api/reviews (client_name, location, message, source, rating, photo_url).


function Avatar({ photoUrl, name }) {
  // https, and a 64px avatar fetched at avatar size rather than the full upload.
  const src = photoUrl ? sizedImageUrl(photoUrl, 64) : null
  if (src) return <img className="rc-avatar" src={src} alt={`${name}, GHL Prime client`} aria-hidden="true" width="64" height="64" loading="lazy" />
  const initial = (name || '?').trim().charAt(0).toUpperCase() || '?'
  return <span className="rc-avatar rc-avatar-initial" aria-hidden="true">{initial}</span>
}

function SourceMark({ source }) {
  if (source === 'OTHER') return null
  const meta = SOURCE_META[source]
  // Sources without a profile page (url: null) show the mark without a link.
  const Tag = meta.url ? 'a' : 'span'
  const linkProps = meta.url ? { href: meta.url, target: '_blank', rel: 'noopener noreferrer' } : {}
  return (
    <Tag
      className={`rc-source${meta.iconOnly ? ' is-icon-only' : ''}`}
      {...linkProps}
      aria-label={`Review from ${meta.longLabel}`}
    >
      {meta.icon}
      {meta.iconOnly ? null : <span>{meta.label}</span>}
    </Tag>
  )
}

// Flag for a recognised country; nothing for "Austin, TX", "Remote", etc. The
// country name sits right beside it, so it is aria-hidden for screen readers
// but still carries alt text for crawlers.
function Flag({ location }) {
  const img = flagImage(countryCode(location))
  if (!img) return null
  return (
    <img
      className="rc-flag"
      src={img.src}
      srcSet={img.srcSet}
      alt={`${location} flag`}
      aria-hidden="true"
      width="20"
      height="15"
      loading="lazy"
      onError={(event) => event.currentTarget.remove()}
    />
  )
}

// The rating given on the source platform; null/absent shows nothing.
function Stars({ rating }) {
  const value = Number(rating)
  if (!Number.isInteger(value) || value < 1) return null
  return (
    <div className="rc-stars" role="img" aria-label={`Rated ${value} out of 5`}>
      {Array.from({ length: 5 }, (_, i) => (
        <Star key={i} size={17} strokeWidth={0} fill="currentColor" className={i < value ? 'is-on' : 'is-off'} aria-hidden="true" />
      ))}
    </div>
  )
}

export function ReviewCard({ review, tint = 'cool' }) {
  const source = sourceKey(review.source)
  const paragraphs = String(review.message || '')
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)

  return (
    <article className={`rc-card rc-${source.toLowerCase()} rc-tint-${tint}`}>
      <header className="rc-head">
        <Avatar photoUrl={review.photo_url} name={review.client_name} />
        <div className="rc-who">
          <p className="rc-name">{review.client_name}</p>
          {review.industry || review.location ? (
            <p className="rc-meta">
              {review.industry ? <span className="rc-industry">{review.industry}</span> : null}
              {review.location ? (
                <span className="rc-location">
                  <Flag location={review.location} />
                  {review.location}
                </span>
              ) : null}
            </p>
          ) : null}
        </div>
        <SourceMark source={source} />
      </header>
      <Stars rating={review.rating} />
      <div className="rc-body">
        {paragraphs.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
    </article>
  )
}

// Masonry wall: CSS columns, so every card keeps its natural height. Every
// third card gets the warm tint, to break up a long run of identical cards.
export default function ReviewWall({ reviews }) {
  return (
    <div className="rc-wall">
      {reviews.map((review, i) => (
        <ReviewCard key={review.id} review={review} tint={i % 3 === 1 ? 'warm' : 'cool'} />
      ))}
    </div>
  )
}
